import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { usePushParams } from "./commons";
import styled from "styled-components";
import apiFetch from "../util/fetch-utils";
import {
    CURRENT_MONITORED_URI_INBOUND_LINKS_FETCHED
} from "../../reducers/currentMonitoredUri";
import { formatDisplayDate } from "./commons";
import { selectLastCrawlId } from '../../selectors/crawlIdSelector';
import UriLink from "./UriLink";
import SearchIcon from "@material-ui/icons/Search";

const MAX_LINKS_PER_PAGE = 10;

function InboundLinks({ uri, isVisible, inboundLinksCount }) {
    const dispatch = useDispatch();
    const pushParams = usePushParams();

    const websiteCrawlId = useSelector(selectLastCrawlId);
    const inboundLinks = useSelector(s => s.currentMonitoredUri.inboundLinks);
    const lastSnapshotDate = useSelector(s => s.currentMonitoredUri.lastSnapshotDate);
    const curSnapshotDate = useSelector(s => s.currentMonitoredUri.curSnapshotDate);

    const [searchInput, setSearchInput] = useState(inboundLinks.curUrlRegex || "");
    const [isFetching, setIsFetching] = useState(false);

    useEffect(() => {
        if (isVisible && uri && uri !== inboundLinks.curUri) {
            fetchInboundLinks(uri, searchInput);
        }
    }, [isVisible, uri, inboundLinks.curUri]);

    const fetchInboundLinks = (u, filter) => {
        if (!websiteCrawlId) return;
        setIsFetching(true);
        const filterQS = filter ? `&urlRegex=.*${filter}.*` : "";
        apiFetch(`/archive-api/page-links/inbound?uri=${encodeURIComponent(u)}&websiteCrawlId=${websiteCrawlId}${filterQS}`)
            .then(r => r.json())
            .then(data => dispatch({
                type: CURRENT_MONITORED_URI_INBOUND_LINKS_FETCHED,
                payload: { inboundLinks: { ...data, curUri: u, curUrlRegex: filter } },
            }))
            .finally(() => setIsFetching(false));
    };

    const isStale = isVisible && uri && inboundLinks.curUri !== uri;
    const isLoading = isFetching || isStale;

    const handleSearch = e => {
        e.preventDefault();
        fetchInboundLinks(uri, searchInput);
    };

    const isLastSnapshotSelected = curSnapshotDate && curSnapshotDate === lastSnapshotDate;

    if (!isLastSnapshotSelected) {
        return (
            <Pad>
                <Notice>
                    <NoticeTitle>Inbound links are only available for the latest crawl</NoticeTitle>
                    <NoticeBody>
                        You're viewing a snapshot from <b>{formatDisplayDate(curSnapshotDate)}</b>.
                        {Number.isInteger(inboundLinksCount)
                            ? <> This page had <b>{inboundLinksCount}</b> inbound link{inboundLinksCount !== 1 ? 's' : ''} at the time.</>
                            : <> We don't have a record of the inbound link count for this snapshot.</>
                        }
                    </NoticeBody>
                    <GoLastBtn onClick={() => pushParams({ date: null })}>
                        Go to latest crawl
                    </GoLastBtn>
                </Notice>
            </Pad>
        );
    }

    return (
        <Pad>
            <SearchForm onSubmit={handleSearch}>
                <SearchInputWrap>
                    <SearchIconWrap><SearchIcon style={{ fontSize: '1rem' }} /></SearchIconWrap>
                    <SearchInput
                        type="text"
                        placeholder="Filter by URL…"
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                    />
                </SearchInputWrap>
                <SearchBtn type="submit">Search</SearchBtn>
            </SearchForm>

            {isLoading ? (
                <LoadingState>
                    <Spinner />
                    <LoadingText>Loading inbound links…</LoadingText>
                </LoadingState>
            ) : (
                <>
                    <CountLine>
                        Showing <b>{Math.min(MAX_LINKS_PER_PAGE, inboundLinks.content?.length ?? 0)}</b> of <b>{inboundLinks.totalElements ?? 0}</b> inbound links
                    </CountLine>

                    {inboundLinks.content?.length > 0 ? (
                        <LinkTable>
                            <thead>
                                <tr>
                                    <Th>Source URL</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {inboundLinks.content.map((row, i) => (
                                    <tr key={i}>
                                        <Td><UriLink url={row} /></Td>
                                    </tr>
                                ))}
                            </tbody>
                        </LinkTable>
                    ) : (
                        <Empty>No inbound links found{searchInput ? ` matching "${searchInput}"` : ''}.</Empty>
                    )}
                </>
            )}
        </Pad>
    );
}

const Pad = styled.div`
    padding: 20px 22px;
`;

const SearchForm = styled.form`
    display: flex;
    gap: 8px;
    margin-bottom: 14px;
`;

const SearchInputWrap = styled.div`
    position: relative;
    flex: 1;
`;

const SearchIconWrap = styled.span`
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
    display: flex;
    align-items: center;
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 7px 12px 7px 32px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.8rem;
    color: #0f172a;
    background: #f8fafc;
    outline: none;
    transition: border-color 120ms;
    box-sizing: border-box;
    &:focus { border-color: #6366f1; background: #fff; }
`;

const SearchBtn = styled.button`
    padding: 7px 16px;
    border-radius: 8px;
    border: none;
    background: #6366f1;
    color: #fff;
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 120ms;
    &:hover { background: #4f46e5; }
`;

const CountLine = styled.div`
    font-size: 0.75rem;
    color: #94a3b8;
    margin-bottom: 12px;
`;

const LinkTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 0.78rem;
`;

const Th = styled.th`
    text-align: left;
    padding: 8px 12px;
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #94a3b8;
    border-bottom: 1px solid #e2e8f0;
`;

const Td = styled.td`
    padding: 9px 12px;
    color: #374151;
    border-bottom: 1px solid #f1f5f9;
    word-break: break-all;
    line-height: 1.4;
    tr:last-child & { border-bottom: none; }
`;



const Empty = styled.div`
    padding: 24px 0;
    font-size: 0.8rem;
    color: #94a3b8;
    text-align: center;
`;

const LoadingState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 36px 0;
`;

const Spinner = styled.div`
    width: 24px;
    height: 24px;
    border: 3px solid #e2e8f0;
    border-top-color: #6366f1;
    border-radius: 50%;
    animation: ilSpin 0.7s linear infinite;
    @keyframes ilSpin { to { transform: rotate(360deg); } }
`;

const LoadingText = styled.div`
    font-size: 0.78rem;
    color: #64748b;
    font-weight: 500;
`;

const Notice = styled.div`
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 20px 22px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const NoticeTitle = styled.div`
    font-size: 0.85rem;
    font-weight: 700;
    color: #0f172a;
`;

const NoticeBody = styled.div`
    font-size: 0.78rem;
    color: #64748b;
    line-height: 1.6;
`;

const GoLastBtn = styled.button`
    align-self: flex-start;
    margin-top: 4px;
    padding: 7px 16px;
    border-radius: 8px;
    border: none;
    background: #6366f1;
    color: #fff;
    font-size: 0.78rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 120ms;
    &:hover { background: #4f46e5; }
`;

export default InboundLinks;
